import { useState, type MouseEvent } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useIsSmallMode } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Accordion, Button, Chip, Text, Truncated } from '@frontend/design-system';
import type {
  AvailabilityMode,
  BookingChannelEnablement,
  OnOpenAddEditTray,
} from '../../../../../components/availability/availability-form/types';
import { OperatoryAvatar } from '../../../../ManageOperatoryV3/operatory-avatar';
import { AvailabilityRuleEntity } from '../../hooks/use-get-availability-rules';
import { AvailabilityRulesTable } from './availability-rules-table';

type Props = {
  availabilityMode: AvailabilityMode;
  availabilityRuleEntity: AvailabilityRuleEntity;
  locationId: string;
  sourceIds?: string[];
  sourceOptions: { label: string; value: string }[];
  openAddEditTray: OnOpenAddEditTray;
  bookingChannelEnablement: BookingChannelEnablement;
  isBookingChannelVisibilityEnabled: boolean;
};

export const AvailabilityRuleListContainer = ({
  openAddEditTray,
  availabilityMode,
  availabilityRuleEntity,
  locationId,
  sourceOptions,
  sourceIds,
  bookingChannelEnablement,
  isBookingChannelVisibilityEnabled,
}: Props) => {
  const { t } = useTranslation('schedule-settings');
  const { isSmallMode } = useIsSmallMode();
  const [accordionValue, setAccordionValue] = useState<string | null>(null);

  const { id, displayName, externalName, isActive, operatoryIcon, sourceId, scheduleDefinitions } =
    availabilityRuleEntity;

  const toggleAccordion = () => {
    setAccordionValue(accordionValue === 'availability-rules' ? null : 'availability-rules');
  };

  return (
    <section css={containerStyles} onClick={toggleAccordion}>
      <header css={headerStyles(isSmallMode, !!displayName)}>
        {availabilityMode === 'OPERATORY' && (
          <div css={avatarColumnStyles}>
            <OperatoryAvatar avatarInfo={operatoryIcon} operatoryName={displayName || externalName || ''} />
          </div>
        )}
        <div
          css={{
            gridColumn: availabilityMode === 'OPERATORY' ? '2' : '1',
            gridRow: '1',
            display: 'flex',
            flexDirection: isSmallMode ? 'column' : 'row',
            alignItems: 'center',
            gap: theme.spacing(1),
          }}
        >
          {displayName && (
            <Truncated as={Text} css={[textStyles(!!isActive), displayNameStyles]}>
              {displayName}
            </Truncated>
          )}
          {externalName && (
            <Truncated as={Text} css={[textStyles(!!isActive), externalNameStyles(isSmallMode, !!displayName)]}>
              {externalName}
            </Truncated>
          )}
          {sourceOptions.length > 0 && (
            <Chip.DataSource variant='neutral' css={{ minWidth: 'fit-content' }}>
              {sourceOptions.map((option) => option.value === sourceId && option.label)}
            </Chip.DataSource>
          )}
        </div>
      </header>
      {isActive && (
        <Accordion
          variant='blank'
          chevronSize={24}
          controlledValue={accordionValue}
          onChange={(val) => setAccordionValue(val)}
        >
          <Accordion.Item value='availability-rules'>
            <Accordion.Header
              css={{
                maxHeight: 40,
                '&[aria-expanded="true"]': { backgroundColor: theme.colors.neutral5 },
                '&:not([aria-expanded="true"])': { borderRadius: theme.borderRadius.medium },
              }}
            >
              {scheduleDefinitions.length > 0 ? (
                <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                  <Text size='medium' weight='bold'>
                    {t('Availability')}
                  </Text>
                  <Text size='small' color='subdued'>
                    {scheduleDefinitions.length}
                  </Text>
                </div>
              ) : accordionValue === 'availability-rules' ? (
                <Chip variant='warn' css={{ minWidth: 'fit-content', backgroundColor: theme.colors.warning10 }}>
                  <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
                    <Icon name='alert-filled-small' />
                    <Text size='medium'>{t('Availability:')}</Text>
                    <Text size='medium' weight='bold'>
                      {0}
                    </Text>
                  </div>
                </Chip>
              ) : (
                <AddAvailabilityButton entityId={id} openAddEditTray={openAddEditTray} />
              )}
            </Accordion.Header>
            <Accordion.Body css={{ padding: 0 }} onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
              <AvailabilityRulesTable
                data={scheduleDefinitions}
                availabilityMode={availabilityMode}
                locationId={locationId}
                openAddEditTray={openAddEditTray}
                entityId={id}
                sourceIds={sourceIds}
                bookingChannelEnablement={bookingChannelEnablement}
                isBookingChannelVisibilityEnabled={isBookingChannelVisibilityEnabled}
              />
              <div css={{ padding: theme.spacing(1.5, 2) }}>
                <AddAvailabilityButton entityId={id} openAddEditTray={openAddEditTray} />
              </div>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      )}
    </section>
  );
};

const AddAvailabilityButton = ({
  entityId,
  openAddEditTray,
}: {
  entityId: string;
  openAddEditTray: OnOpenAddEditTray;
}) => {
  const { t } = useTranslation('schedule-settings');
  return (
    <Button
      variant='tertiary'
      iconName='add'
      size='small'
      css={{ padding: theme.spacing(1, 0.25) }}
      onClick={(e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        openAddEditTray(null, entityId, true);
      }}
    >
      {t('Add Availability')}
    </Button>
  );
};

const containerStyles = {
  marginBottom: theme.spacing(4),
  border: `1px solid ${theme.colors.neutral30}`,
  borderRadius: theme.borderRadius.medium,
  cursor: 'pointer',
};

const headerStyles = (isSmallMode: boolean, hasDisplayName: boolean) =>
  css({
    display: 'grid',
    gridTemplateColumns: 'minmax(32px, 64px) auto',
    gridTemplateRows: hasDisplayName && isSmallMode ? 'auto auto' : 'auto',
    justifyContent: 'start',
    alignItems: 'center',
    padding: theme.spacing(1.25, 2),
    borderBottom: `1px solid ${theme.colors.neutral20}`,
  });

const avatarColumnStyles = {
  display: 'flex',
  alignItems: 'center',
  gridRow: '1 / -1',
};

const displayNameStyles = {
  gridColumn: '2',
  gridRow: '1',
};

const externalNameStyles = (isSmallMode: boolean, hasDisplayName: boolean) => ({
  gridColumn: '2',
  gridRow: hasDisplayName && isSmallMode ? '2' : '1',
});

const textStyles = (isActive: boolean) => ({
  color: isActive ? theme.colors.neutral90 : theme.colors.neutral40,
  minWidth: 180,
  maxWidth: 240,
});
